import React, { useEffect, useState, memo } from "react";
import { FormGroup, Label, Input, Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  useDetailQuery,
  useCreateMutation,
  useUpdateMutation,
} from "helpers/http-client/hooks/useCrud";
import { API_ENDPOINTS } from "helpers/http-client/httpConstants";

interface ManufacturerFormProps {
  manufacturerId?: string;
  onSuccess: () => void;
}

export interface ManufacturerFormValues {
  name: string;
  description?: string;
  logoUrl?: string;
  country?: string;
}

const ManufacturerForm: React.FC<ManufacturerFormProps> = ({ manufacturerId, onSuccess }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(manufacturerId);
  const url = isEdit ? `${API_ENDPOINTS.MANUFACTURERS}/${manufacturerId}` : "";

  const { data, isLoading } = useDetailQuery<ManufacturerFormValues>(url, manufacturerId);

  const [formValues, setFormValues] = useState<ManufacturerFormValues>({
    name: "",
    country: "",
    description: "",
    logoUrl: "",
  });

  const { mutate: createManufacturer } = useCreateMutation(API_ENDPOINTS.MANUFACTURERS);
  const { mutate: updateManufacturer } = useUpdateMutation(API_ENDPOINTS.MANUFACTURERS);

  useEffect(() => {
    if (data && isEdit) {
      setFormValues({
        name: data.name || "",
        country: data.country || "",
        description: data.description || "",
        logoUrl: data.logoUrl || "",
      });
    }
  }, [data, isEdit]);

  const handleChange = (field: keyof ManufacturerFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const payload: ManufacturerFormValues = {
      name: formValues.name.trim(),
      country: formValues.country?.trim() || undefined,
      description: formValues.description?.trim() || undefined,
      logoUrl: formValues.logoUrl?.trim() || undefined,
    };

    if (isEdit && manufacturerId) {
      updateManufacturer({ id: manufacturerId, data: payload }, { onSuccess });
    } else {
      createManufacturer(payload, { onSuccess });
    }
  };

  if (isEdit && isLoading) {
    return <Spinner color="primary" />;
  }

  return (
    <>
      <FormGroup>
        <Label>{t("Name")}</Label>
        <Input
          value={formValues.name}
          placeholder={t("Enter manufacturer name")}
          onChange={(e) => handleChange("name", e.target.value)}
          required
        />
      </FormGroup>

      <FormGroup>
        <Label>{t("Country")}</Label>
        <Input
          value={formValues.country || ""}
          placeholder={t("Country")}
          onChange={(e) => handleChange("country", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>{t("Logo URL")}</Label>
        <Input
          value={formValues.logoUrl || ""}
          placeholder={t("Logo URL")}
          onChange={(e) => handleChange("logoUrl", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>{t("Description")}</Label>
        <Input
          type="textarea"
          value={formValues.description || ""}
          placeholder={t("Description")}
          onChange={(e) => handleChange("description", e.target.value)}
          rows={4}
        />
      </FormGroup>

      <div className="d-flex justify-content-end">
        <Button color="primary" onClick={handleSubmit}>
          {isEdit ? t("Update") : t("Save")}
        </Button>
      </div>
    </>
  );
};

export default memo(ManufacturerForm);
